import { useSessionStorage } from '@mantine/hooks';
import { useRef, useState } from 'react';

export const STANDARD_SIDEBAR_WIDTH = 430;
const CONTENT_MIN_WIDTH = 430;
const COLLAPSE_THRESHOLD = 100;
const FLEX_GAP = 4;
const STORAGE_KEY = 'app-sidebar-width';

type DragPreview = 'collapse-sidebar' | 'fullscreen-sidebar' | null;

type Props = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  onCollapse: () => void;
  onFullscreen: () => void;
};

export function useSidebarResize({ containerRef, onCollapse, onFullscreen }: Props) {
  const [sidebarWidth, setSidebarWidth] = useSessionStorage({
    key: STORAGE_KEY,
    defaultValue: STANDARD_SIDEBAR_WIDTH,
    deserialize: (value: string | undefined) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : STANDARD_SIDEBAR_WIDTH;
    },
  });

  const [isDragging, setIsDragging] = useState(false);
  const [dragPreview, setDragPreview] = useState<DragPreview>(null);
  const isDraggingRef = useRef(false);
  const dragPreviewRef = useRef<DragPreview>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!containerRef.current) return;

    isDraggingRef.current = true;
    setIsDragging(true);
    document.body.style.userSelect = 'none';

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!containerRef.current || !isDraggingRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const maxWidth = Math.max(containerRect.width * 0.5, containerRect.width - CONTENT_MIN_WIDTH);
      const rawWidth = containerRect.right - moveEvent.clientX;
      const contentRemaining = containerRect.width - rawWidth - FLEX_GAP;

      let preview: DragPreview = null;
      if (rawWidth < STANDARD_SIDEBAR_WIDTH - COLLAPSE_THRESHOLD) {
        preview = 'collapse-sidebar';
      } else if (contentRemaining < CONTENT_MIN_WIDTH - COLLAPSE_THRESHOLD) {
        preview = 'fullscreen-sidebar';
      }

      dragPreviewRef.current = preview;
      setDragPreview(preview);

      const clamped = Math.max(STANDARD_SIDEBAR_WIDTH, Math.min(maxWidth, rawWidth));
      setSidebarWidth(clamped);
    };

    const handleMouseUp = () => {
      const finalPreview = dragPreviewRef.current;

      isDraggingRef.current = false;
      dragPreviewRef.current = null;
      setIsDragging(false);
      setDragPreview(null);
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      if (finalPreview === 'collapse-sidebar') {
        onCollapse();
      } else if (finalPreview === 'fullscreen-sidebar') {
        onFullscreen();
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return { sidebarWidth, isDragging, handleMouseDown, dragPreview };
}
